import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import 'moment/locale/vi';
import dictionary from './dictionary';

const STORAGE_KEY = '@social-control/lang';
const SUPPORTED = ['vi', 'en'];
const DEFAULT_LANG = 'en';

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: key => key,
});

function readInitial() {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED.includes(saved)) return saved;
  } catch (e) {
    // ignore — fall back to default
  }
  return DEFAULT_LANG;
}

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : ''));
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch (e) {
      // ignore
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
    // Keep moment's locale in sync with the UI language so relative times
    // (fromNow, calendar) match the toggle — 'en' is moment's built-in default.
    moment.locale(lang === 'vi' ? 'vi' : 'en');
  }, [lang]);

  const setLang = useCallback(next => {
    if (SUPPORTED.includes(next)) setLangState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const table = dictionary[lang] || dictionary[DEFAULT_LANG];
      const fallback = dictionary[DEFAULT_LANG];
      const raw = table[key] != null ? table[key] : fallback[key];
      if (raw == null) return key;
      return interpolate(raw, vars);
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useTranslation() {
  return useContext(LanguageContext);
}

export const SUPPORTED_LANGUAGES = SUPPORTED;
